class SwiperConfigurator {
    static swiperInstances = new Map();

    constructor(sliderElement) {
        this.element = sliderElement;
        this.options = this.parseConfig();
        this.setupEventListeners();

        if (this.options.observer) {
            this.initIntersectionObserver();
        } else {
            this.initializeSwiper();
        }
    }

    parseConfig() {
        const { dataset: { config, mobile, observer } } = this.element;

        try {
            return {
                config: JSON.parse(config),
                mobile: mobile === "true",
                observer: observer === "true"
            };
        } catch (error) {
            console.error("Error parsing slider configuration:", error);
            return { config: {}, mobile: false, observer: false };
        }
    }

    initializeSwiper() {
        if (this.shouldDisableSwiper()) {
            return;
        }
        if (!Swiper || !this.element || this.element.swiper) {
            return;
        }
        if (this.options.config.thumbs?.swiper) {
            const thumbsSelector = this.options.config.thumbs.swiper;
            const thumbsElement = document.querySelector(thumbsSelector);
            const thumbsInstance = SwiperConfigurator.swiperInstances.get(thumbsElement);

            if (!thumbsInstance) {
                setTimeout(() => this.initializeSwiper(),0);
                return;
            }

            this.options.config.thumbs.swiper = thumbsInstance;
        }

        const instance = new Swiper(this.element, this.options.config);
        SwiperConfigurator.swiperInstances.set(this.element, instance)
    }

    shouldDisableSwiper() {
        return this.options.mobile && window.innerWidth > 768;
    }

    setupEventListeners() {
        window.addEventListener("resize", this.handleResize.bind(this));
    }

    handleResize() {
      if (this.shouldDisableSwiper()) {
        this.destroySwiper();
      } else {
        [...this.element.swiper.navigation.$prevEl, ...this.element.swiper.navigation.$nextEl].forEach(button =>{
          ['swiper-button-disabled','swiper-button-lock'].forEach(clas => button.classList.remove(clas));
          ['disabled','aria-disabled'].forEach(clas => button.removeAttribute(clas))
        })
        this.destroySwiper();
        this.initializeSwiper();

      }
    }

    destroySwiper() {
        if (this.element && this.element.swiper) {
            this.element.swiper.destroy(true, true);
        }
    }

    initIntersectionObserver() {
        this.observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    this.observer.disconnect();
                    this.initializeSwiper();
                }
            });
        }, { threshold: 0.1 });

        this.observer.observe(this.element);
    }

    static appendTagToBody(tagType, filePath) {
        return new Promise((resolve, reject) => {
            const tag = document.createElement(tagType === 'script' ? 'script' : 'link');

            if (tagType === 'script') {
                tag.src = filePath;
                tag.type = 'text/javascript';
            } else if (tagType === 'style') {
                tag.href = filePath;
                tag.rel = 'stylesheet';
            } else {
                return reject(new Error('Unsupported tag type. Please use "script" or "style".'));
            }

            tag.onload = resolve;
            tag.onerror = () => reject(new Error(`Failed to load ${tagType} file: ${filePath}`));

            document.body.appendChild(tag);
        });
    }

    static appendSwiperFiles() {
        const { css, js } = window.swiperFile;
        const files = [
            { type: 'style', path: css },
            { type: 'script', path: js }
        ].filter(file => file.path);

        if (files.length === 0) {
            return Promise.resolve();
        }

        const loadFilePromises = files.map(file => SwiperConfigurator.appendTagToBody(file.type, file.path));
        return Promise.all(loadFilePromises);
    }
}

// ['DOMContentLoaded', 'reinitSliders'].forEach(event => {
  document.addEventListener('DOMContentLoaded', () => {
      const sliderElements = document.querySelectorAll("[data-slider-selector]");

      if (sliderElements.length > 0) {
          SwiperConfigurator.appendSwiperFiles()
              .then(() => {
                  sliderElements.forEach(slider => new SwiperConfigurator(slider));
              }).catch((error) => {
              console.error('Failed to load Swiper files:', error);
          });
      }
  });
// })

class PopupTrigger extends HTMLElement {
  constructor() {
    super();  

    this.popupId = this.dataset.popupId;
    this.popupOverlay = document.querySelector(`div.popup__overlay[data-popup-id="${this.popupId}"]`);
    this.popup = this.popupOverlay?.querySelector('.popup__content');
    this.closeButton = this.popup?.querySelector('.header__icon-close');
  }

  connectedCallback() {
    if (!this.popupOverlay || !this.popup) {
      console.warn(`Popup with id "${this.popupId}" not found.`);
      return;
    }

    this.addEventListener('click', this.openPopup.bind(this));

    this.popupOverlay.addEventListener('click', this.closePopup.bind(this));

    this.popup.addEventListener('click', (event) => {
      event.stopPropagation();
    });    

    if (this.closeButton) 
      this.closeButton.addEventListener('click', this.closePopup.bind(this));
  }

  disconectedCallback() {
    this.removeEventListener('click');
    this.popupOverlay.removeEventListener('click')
    this.popup.removeEventListener('click')

    if (this.closeButton) 
      this.closeButton.removeEventListener('click')
  }

  openPopup() {
    document.body.style.overflow = 'hidden';
    this.popupOverlay.setAttribute("data-popup-open", "");
  }

  closePopup() {
    document.body.style.overflow = '';
    this.popupOverlay?.removeAttribute("data-popup-open");
  }
}

if(!customElements.get('popup-trigger')) {
  customElements.define('popup-trigger', PopupTrigger);
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();

    this.minusButton = this.querySelector('.quantity__button--minus');
    this.plusButton = this.querySelector('.quantity__button--plus');
    this.inputElement = this.querySelector('.quantity__input');
  }

  connectedCallback() {
    if (!this.minusButton || !this.plusButton || !this.inputElement) {
      return console.warn('QuantityInput: required elements not found.');
    }

    this.min = parseInt(this.inputElement.getAttribute('min') || '1');

    const inputValue = parseInt(this.inputElement.value) || this.min

    this.minusClickHandler = () => this.updateValue(-1);
    this.plusClickHandler = () => this.updateValue(1);
    this.inputHandler = () => this.syncManualInput();

    this.minusButton.addEventListener('click', this.minusClickHandler);
    this.plusButton.addEventListener('click', this.plusClickHandler);
    this.inputElement.addEventListener('input', this.inputHandler);

    this.minusButton.classList.toggle('quantity__button--hidden', inputValue <= this.min)
  }

  disconnectedCallback() {
    this.minusButton.removeEventListener('click', this.minusClickHandler);
    this.plusButton.removeEventListener('click', this.plusClickHandler);
    this.inputElement.removeEventListener('input', this.inputHandler);
  }

  updateValue(delta) {
    const currentValue = parseInt(this.inputElement.value) || this.min;
    const newValue = Math.max(this.min, currentValue + delta);

    this.minusButton.classList.toggle('quantity__button--hidden', newValue <= this.min)

    this.setValue(newValue);
  }

  syncManualInput() {
    const inputValue = parseInt(this.inputElement.value) || this.min;
      
    this.setValue(inputValue);
  }

  setValue(value) {
    this.inputElement.value = value;
    this.inputElement.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

if (!customElements.get('quantity-input')) {
  customElements.define('quantity-input', QuantityInput);
}

class ZoomImage extends HTMLElement {
  constructor() {
    super();
    
    this.image = this.querySelector('img');
    this.isZoomed = false;

    if (!this.image) {
      console.error('ZoomImage: image tag not found inside zoom-image');
    }
  }

  connectedCallback() {
    this.setupStyles();
    this.bindEvents();

    this.addEventListener("mouseleave", this.resetZoom.bind(this));
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
    this.removeEventListener('mousemove', this.handleMouseMove);
  }

  setupStyles() {
    this.style.display = 'block';
    this.style.overflow = 'hidden';

    this.image.style.transition = 'transform 0.2s ease-out';
    this.image.style.willChange = 'transform';
  }

  bindEvents() {
    this.handleClick = (event) => this.toggleZoom(event);
    this.handleMouseMove = (event) => this.handleMove(event);

    this.addEventListener('click', this.handleClick);
    this.addEventListener('mousemove', this.handleMouseMove);
  }

  toggleZoom(event) {
    this.isZoomed = !this.isZoomed;

    if (this.isZoomed) {
      this.classList.add('zoomed');
      this.image.style.transform = 'scale(2)';
      this.updateTransformOrigin(event);
    } else {
      this.classList.remove('zoomed');
      this.image.style.transform = 'scale(1)';
    }
  }

  handleMove(event) {
    if (!this.isZoomed) return;
    this.updateTransformOrigin(event);
  }

  updateTransformOrigin(event) {
    const rect = this.getBoundingClientRect();

    const x = ((event.clientX - rect.left) / rect.width) * 100;
    const y = ((event.clientY - rect.top) / rect.height) * 100;

    this.image.style.transformOrigin = `${x}% ${y}%`;
  }

  resetZoom() {
    if (!this.isZoomed) return;

    this.isZoomed = false;
    this.classList.remove("zoomed");
    this.image.style.transform = "scale(1)";
    this.image.style.transformOrigin = "center center";
  }
}

if(!customElements.get('zoom-image')) {
    customElements.define('zoom-image', ZoomImage);
}

class HeaderDrawer extends HTMLElement {
  constructor() {
    super();

    this.toggleBtn = this.querySelector('.drawer-toggle');
    this.drawer = this.querySelector('.drawer');

    this.toggle = this.toggle.bind(this);
    this.close = this.close.bind(this);
    this.onKeyUp = this.onKeyUp.bind(this);
    this.onClickOutside = this.onClickOutside.bind(this);
    this.onLinkClick = this.onLinkClick.bind(this);
  }

  connectedCallback() {
    this.toggleBtn.addEventListener('click', this.toggle);
    document.addEventListener('keyup', this.onKeyUp);
    document.addEventListener('click', this.onClickOutside);
    window.addEventListener('resize', this.updateBreakpoint);
    this.drawer.addEventListener('click', this.onLinkClick);

    this.updateBreakpoint();
  }

  disconnectedCallback() {
    this.toggleBtn.removeEventListener('click', this.toggle);
    document.removeEventListener('keyup', this.onKeyUp);
    document.removeEventListener('click', this.onClickOutside);
    window.removeEventListener('resize', this.updateBreakpoint);
    this.drawer.removeEventListener('click', this.onLinkClick);
  }

  updateBreakpoint = () => {
    const width = window.innerWidth;

    const newBreakpoint = width >= 750 ? 'desktop' : 'mobile';

    if (this.currentBreakpoint !== newBreakpoint) {
      this.currentBreakpoint = newBreakpoint;
      this.setAttribute('data-breakpoint', newBreakpoint);
    }
  }

  toggle() {
    this.drawer.classList.contains('open') ? this.close() : this.open();
  }

  open() {
    this.drawer.classList.add('open');
    document.body.classList.add('drawer-open');

    requestAnimationFrame(() => {
      this.drawer.classList.add('animate-in');
    });
  }

  close() {
    this.drawer.classList.remove('animate-in');
    this.drawer.classList.add('animate-out');

    this.drawer.addEventListener(
      'animationend',
      () => {
        this.drawer.classList.remove('open', 'animate-out');
      },
      { once: true }
    );

    document.body.classList.remove('drawer-open');
  }

  onKeyUp(e) {
    if (e.code === 'Escape') this.close();
  }

  onClickOutside(e) {
    if (!this.drawer.classList.contains('open')) return;

    const clickInsideDrawer = e.target.closest('.drawer');
    const clickOnToggle = e.target.closest('.drawer-toggle');

    if (!clickInsideDrawer && !clickOnToggle) {
      this.close();
    }
  }

  onLinkClick(e) {
    const link = e.target.closest('a');
    if (!link) return;

    this.close();
  }
}

customElements.define('header-drawer', HeaderDrawer);

class CountdownTimer extends HTMLElement {
  constructor() {
    super();

    this.intervalId = null;
    this.targetDate = null;
    this.timerMode = 'fixed';
    this.inputFormat = 'mdy';
    this.durationMs = 2 * 60 * 60 * 1000;
    this.storageKey = '';
  }

  connectedCallback() {
    this.timerEl = this.querySelector('[data-countdown]');

    if (!this.timerEl) return;

    this.hoursEl = this.timerEl.querySelector('[data-countdown-hours]');
    this.minutesEl = this.timerEl.querySelector('[data-countdown-minutes]');
    this.secondsEl = this.timerEl.querySelector('[data-countdown-seconds]');
    this.targetDateDisplayEl = this.querySelector('[data-target-date-display]');

    this.timerMode = this.dataset.timerMode === 'rolling' ? 'rolling' : 'fixed';
    this.inputFormat = this.dataset.inputFormat || 'mdy';

    if (this.timerMode === 'rolling') {
      this.durationMs = this.getDurationMs();
      this.storageKey = this.getStorageKey();
      this.targetDate = this.getRollingTargetDate();
    } else {
      const targetDateRaw = (this.dataset.targetDate || '').trim();
      this.targetDate = this.parseTargetDate(targetDateRaw, this.inputFormat);

      if (!this.targetDate) {
        this.setCountdown(0, 0, 0);

        if (this.targetDateDisplayEl) {
          this.targetDateDisplayEl.textContent = 'Invalid date';
        }

        return;
      }
    }

    if (this.targetDateDisplayEl) {
      this.targetDateDisplayEl.textContent = this.formatDateForDisplay(this.targetDate, this.inputFormat);
    }

    this.updateCountdown();
    this.intervalId = window.setInterval(() => this.updateCountdown(), 1000);
  }

  disconnectedCallback() {
    if (this.intervalId) {
      window.clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  updateCountdown() {
    if (!this.targetDate) return;

    const now = Date.now();
    let diffMs = this.targetDate.getTime() - now;

    if (diffMs <= 0) {
      if (this.timerMode === 'rolling') {
        this.targetDate = this.createRollingTargetDate();
        this.storeRollingTargetDate(this.targetDate);
        diffMs = this.targetDate.getTime() - now;

        if (this.targetDateDisplayEl) {
          this.targetDateDisplayEl.textContent = this.formatDateForDisplay(this.targetDate, this.inputFormat);
        }
      } else {
        this.setCountdown(0, 0, 0);

        if (this.intervalId) {
          window.clearInterval(this.intervalId);
          this.intervalId = null;
        }

        return;
      }
    }

    const totalSeconds = Math.floor(diffMs / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    this.setCountdown(hours, minutes, seconds);
  }

  setCountdown(hours, minutes, seconds) {
    this.hoursEl.textContent = `${this.pad(hours)}:`;
    this.minutesEl.textContent = `${this.pad(minutes)}:`;
    this.secondsEl.textContent = this.pad(seconds);
  }

  pad(value) {
    return String(value).padStart(2, '0');
  }

  getDurationMs() {
    const hours = Number(this.dataset.durationHours || 2);

    if (!Number.isFinite(hours) || hours <= 0) {
      return 2 * 60 * 60 * 1000;
    }

    return Math.floor(hours * 60 * 60 * 1000);
  }

  getStorageKey() {
    const sectionId = this.dataset.sectionId || 'global';
    const pathname = window.location.pathname || '/';
    return `countdown-timer:${sectionId}:${pathname}`;
  }

  getRollingTargetDate() {
    const storedTarget = this.getStoredRollingTargetDate();

    if (storedTarget && storedTarget.getTime() > Date.now()) {
      return storedTarget;
    }

    const newTarget = this.createRollingTargetDate();
    this.storeRollingTargetDate(newTarget);
    return newTarget;
  }

  getStoredRollingTargetDate() {
    if (!this.storageKey || !window.localStorage) return null;

    try {
      const raw = window.localStorage.getItem(this.storageKey);
      if (!raw) return null;

      const timestamp = Number(raw);
      if (!Number.isFinite(timestamp) || timestamp <= 0) return null;

      return new Date(timestamp);
    } catch (error) {
      console.error('CountdownTimer: failed to read localStorage target date', error);
      return null;
    }
  }

  createRollingTargetDate() {
    return new Date(Date.now() + this.durationMs);
  }

  storeRollingTargetDate(date) {
    if (!this.storageKey || !window.localStorage || !date) return;

    try {
      window.localStorage.setItem(this.storageKey, String(date.getTime()));
    } catch (error) {
      console.error('CountdownTimer: failed to store localStorage target date', error);
    }
  }

  parseTargetDate(raw, format) {
    if (!raw) return null;

    const normalized = raw.replace(/\s+/g, ' ').trim();

    if (format === 'ymd') {
      return this.parseByRegex(
        normalized,
        /^(\d{4})-(\d{1,2})-(\d{1,2})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?)?$/,
        [1, 2, 3]
      );
    }

    if (format === 'dmy') {
      return this.parseByRegex(
        normalized,
        /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?)?$/,
        [3, 2, 1]
      );
    }

    return this.parseByRegex(
      normalized,
      /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?)?$/,
      [3, 1, 2]
    );
  }

  parseByRegex(value, regex, map) {
    const match = value.match(regex);

    if (!match) return null;

    const year = Number(match[map[0]]);
    const month = Number(match[map[1]]);
    const day = Number(match[map[2]]);
    const hour = Number(match[4] || 0);
    const minute = Number(match[5] || 0);
    const second = Number(match[6] || 0);

    const date = new Date(year, month - 1, day, hour, minute, second);

    if (
      date.getFullYear() !== year ||
      date.getMonth() !== month - 1 ||
      date.getDate() !== day ||
      date.getHours() !== hour ||
      date.getMinutes() !== minute ||
      date.getSeconds() !== second
    ) {
      return null;
    }

    return date;
  }

  formatDateForDisplay(date, format) {
    const year = date.getFullYear();
    const month = this.pad(date.getMonth() + 1);
    const day = this.pad(date.getDate());
    const hours = this.pad(date.getHours());
    const minutes = this.pad(date.getMinutes());

    if (format === 'ymd') {
      return `${year}-${month}-${day} ${hours}:${minutes}`;
    }

    if (format === 'dmy') {
      return `${day}/${month}/${year} ${hours}:${minutes}`;
    }

    return `${month}/${day}/${year} ${hours}:${minutes}`;
  }
}

if (!customElements.get('countdown-timer')) {
  customElements.define('countdown-timer', CountdownTimer);
}

class LandingBundleAtc extends HTMLElement {
  constructor() {
    super();

    this.buttonEl = null;
    this.errorEl = null;
    this.defaultButtonText = '';
    this.isLoading = false;
  }

  connectedCallback() {
    this.buttonEl = this.querySelector('[data-bundle-atc-button]');
    this.errorEl = this.querySelector('[data-bundle-atc-error]');

    if (!this.buttonEl) return;

    this.defaultButtonText = this.buttonEl.textContent.trim();
    this.clickHandler = this.handleClick.bind(this);
    this.buttonEl.addEventListener('click', this.clickHandler);
  }

  disconnectedCallback() {
    if (this.buttonEl && this.clickHandler) {
      this.buttonEl.removeEventListener('click', this.clickHandler);
    }
  }

  async handleClick() {
    if (this.isLoading) return;

    this.hideError();
    const items = this.getItems();

    if (!items.length) {
      this.showError(this.dataset.emptyText || 'Select products in section settings.');
      return;
    }

    this.isLoading = true;
    this.setButtonState(true, this.dataset.loadingText || 'Adding...');

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ items })
      });

      if (!response.ok) {
        throw new Error('Add to cart failed');
      }

      const redirect = this.dataset.redirect || 'checkout';

      if (redirect === 'checkout') {
        window.location.href = '/checkout';
        return;
      }

      if (redirect === 'cart') {
        window.location.href = '/cart';
        return;
      }

      this.setButtonState(false, this.defaultButtonText);
    } catch (error) {
      console.error('Landing bundle add-to-cart error:', error);
      this.showError(this.dataset.errorText || 'Could not add products. Try again.');
      this.setButtonState(false, this.defaultButtonText);
    } finally {
      this.isLoading = false;
    }
  }

  getItems() {
    const raw = this.dataset.variants || '[]';

    try {
      const parsed = JSON.parse(raw);

      if (!Array.isArray(parsed)) return [];

      return parsed
        .map(item => {
          const normalizedItem = {
            id: Number(item.id),
            quantity: Number(item.quantity || 1)
          };

          const sellingPlan = Number(item.selling_plan);
          if (Number.isFinite(sellingPlan) && sellingPlan > 0) {
            normalizedItem.selling_plan = sellingPlan;
          }

          const parentId = Number(item.parent_id);
          if (Number.isInteger(parentId) && parentId > 0) {
            normalizedItem.parent_id = parentId;
          }

          return normalizedItem;
        })
        .filter(item => Number.isInteger(item.id) && item.id > 0 && Number.isInteger(item.quantity) && item.quantity > 0);
    } catch (error) {
      console.error('Invalid bundle variants JSON:', error);
      return [];
    }
  }

  setButtonState(disabled, text) {
    if (!this.buttonEl) return;
    this.buttonEl.disabled = disabled;
    this.buttonEl.textContent = text;
  }

  showError(message) {
    if (!this.errorEl) return;
    this.errorEl.hidden = false;
    this.errorEl.textContent = message;
  }

  hideError() {
    if (!this.errorEl) return;
    this.errorEl.hidden = true;
    this.errorEl.textContent = '';
  }
}

if (!customElements.get('landing-bundle-atc')) {
  customElements.define('landing-bundle-atc', LandingBundleAtc);
}
